import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  EnvironmentInjector,
  createComponent,
  inject,
  input,
  makeEnvironmentProviders,
  output,
  provideAppInitializer,
  type EnvironmentProviders,
} from '@angular/core';
import { UI_TEXT } from '../i18n';
import { ScDialogComponent } from './dialog.component';

/** Confirmación «Sí / No» (reemplaza a `confirm` de devextreme/ui/dialog). */
@Component({
  selector: 'sck-confirm',
  imports: [ScDialogComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <sc-dialog
      [open]="true"
      [title]="title()"
      [showCloseButton]="false"
      initialFocus="first"
      panelClass="sck-confirm"
      (closed)="answer.emit(false)"
    >
      <div class="sck-confirm__message">
        @for (paragraph of paragraphs(); track $index) {
          <!-- En una línea: sin espacios entre las líneas y los <br>. -->
          <!-- prettier-ignore -->
          <p>@for (line of lines(paragraph); track $index; let last = $last) {{{ line }}@if (!last) {<br />}}</p>
        }
      </div>
      <div class="sck-confirm__buttons">
        <button type="button" class="sck-confirm__button" (click)="answer.emit(true)">
          {{ text.yes }}
        </button>
        <button type="button" class="sck-confirm__button" (click)="answer.emit(false)">
          {{ text.no }}
        </button>
      </div>
    </sc-dialog>
  `,
})
export class ScConfirmComponent {
  readonly title = input('');
  readonly paragraphs = input<string[]>([]);
  readonly answer = output<boolean>();
  protected readonly text = UI_TEXT;

  /** Las líneas de un párrafo (un salto de línea dentro del párrafo es un <br>). */
  protected lines(paragraph: string): string[] {
    return paragraph.split(/\r?\n/);
  }
}

let kitInjector: EnvironmentInjector | null = null;

/** Registra el inyector de la aplicación para las confirmaciones (`scConfirm` sin inyector). */
export function provideScKit(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      kitInjector = inject(EnvironmentInjector);
    }),
  ]);
}

/**
 * Abre una confirmación y resuelve `true` con «Sí» y `false` con «No» o Escape. Los párrafos son
 * texto (no HTML); un `\n` dentro de un párrafo es un salto de línea.
 */
export function scConfirm(
  options: { title: string; paragraphs: string[] },
  injector: EnvironmentInjector | null = kitInjector,
): Promise<boolean> {
  if (!injector)
    throw new Error('scConfirm: falta provideScKit() en la configuración de la aplicación');
  const appRef = injector.get(ApplicationRef);
  const ref = createComponent(ScConfirmComponent, { environmentInjector: injector });
  ref.setInput('title', options.title);
  ref.setInput('paragraphs', options.paragraphs);
  appRef.attachView(ref.hostView);
  return new Promise<boolean>((resolve) => {
    const subscription = ref.instance.answer.subscribe((answer) => {
      subscription.unsubscribe();
      appRef.detachView(ref.hostView);
      ref.destroy();
      resolve(answer);
    });
  });
}
